/**
 * =============================================================================
 * elementKind.js — the ONE place that reads `kind`
 * =============================================================================
 *
 * An element is `{ kind, origin, struck, content }`. Three separate decisions
 * are made from those four fields, and all three live here:
 *
 *   forDisplay(e)   what string goes to renderMathIn
 *   isMathField(e)  which field type the editor gives it
 *   strikeStyle(e)  whether it renders struck through
 *
 * WHY ONE MODULE. `kind` carries two independent facts — `text`/`math` describe
 * NOTATION, `note` describes PLACEMENT — and the model must pick one, so
 * choosing `note` destroys the notation fact. The fix for that is at the
 * transcriber prompt, not here (see "The transcriber is not finished"). When it
 * lands, it must be ONE edit. Two call sites reading `kind` independently would
 * be two edits that have to agree.
 *
 * NOTHING ELSE IN THE APP MAY BRANCH ON `kind`. If a component needs to know
 * what an element looks like, it asks this module.
 *
 * INSTRUCTIONS ARE NOT ELEMENTS. They are `{ id, content }` — no kind, no
 * origin, no struck. They are prose, they always get a plain text input, and
 * they never come through here. A `kind: note` element rendered as an
 * instruction is CONVERTED to that shape, which is where it loses its origin
 * dropdown (a known, accepted consequence — see 1.4).
 * =============================================================================
 */

// ── DISPLAY ─────────────────────────────────────────────────────────────────
/**
 * The string handed to `renderMathIn`.
 *
 * The model returns pure-maths lines BARE, and renderMathInElement only
 * typesets what sits between delimiters — so `\frac{x - 6}{2}` would otherwise
 * appear on screen exactly as written rather than as a fraction. This wraps it.
 *
 * The `includes('$')` guard is what lets delimited and bare content coexist.
 * The transcriber prompt already asks for `$…$` and the model ignores it; when
 * that is fixed, content arrives already delimited and passes through
 * untouched. No client change needed on that day.
 *
 * `kind: note` and `kind: text` are NOT wrapped. Wrapping a note would set
 * `PEMDAS` as six italic variables; leaving it bare shows a boxed answer
 * containing `\frac` as literal backslashes. Both are wrong and the field
 * cannot tell them apart — the milder failure is chosen deliberately.
 */
export function forDisplay(e) {
  if (!e) return '';
  const content = e.content == null ? '' : String(e.content);
  return e.kind === 'math' && !content.includes('$') ? `$${content}$` : content;
}

// ── EDITOR FIELD TYPE ───────────────────────────────────────────────────────
/**
 * True when the editor gives this element a `math-field`.
 *
 * Everything else gets a plain text input. A `math-field` treats its WHOLE
 * value as LaTeX, so a prose line put in one is set as a product of italic
 * variables — and because typing saves live, whatever the field serialises back
 * BECOMES the draft and is what the classifier receives on the resubmit. Not
 * cosmetic.
 *
 * A `kind: text` transcribed line gets a plain input even when it carries
 * mathematics. The Calculus page's problem statements arrive that way; the
 * student sees the same characters they would anywhere else.
 */
export function isMathField(e) {
  return !!e && e.kind === 'math';
}

// ── STRUCK ──────────────────────────────────────────────────────────────────
/**
 * Style for a struck line in DISPLAY. Null when not struck, so it can be
 * spread into a style object unconditionally.
 *
 * A struck line is still fully editable in the editor, with its origin dropdown
 * and its `×` like any other, and it is still sent. A student who crossed
 * something out may have crossed out the right thing.
 */
export function strikeStyle(e) {
  return e && e.struck ? { textDecoration: 'line-through' } : null;
}

/**
 * ── MIXED TEXT AND MATHS IN ONE FIELD ──────────────────────────────────────
 *
 * A role field is a `<math-field>` with `default-mode="text"` and
 * `smart-mode`, so it behaves as a TEXT box that flips into maths when maths is
 * typed — the docs' own example is typing "if x > 0". One box, no segments to
 * tab between, no caret stranded at a boundary.
 *
 * WRITING IS FREE. In text mode MathLive parses `$…$` on the way in, so the
 * contract string goes straight into `mf.value` untouched. That replaced an
 * earlier hack that wrapped whole lines in a \text{} group.
 *
 * READING NEEDS A CONVERSION, because the two are duals:
 *
 *   what we store   If $f(x)$ is increasing      text with maths islands
 *   what a field    \text{If }f(x)\text{ is …}   maths with text islands
 *
 * Both are valid LaTeX and both render. We keep OUR form, because the
 * classifier reads these strings and an 8B does better with prose punctuated by
 * `$` than with `\text{}` on every clause — and because `instructions_used`,
 * the classifier prompt and the transcriber are all built on it. The conversion
 * is confined to the one place it is needed: reading a field back.
 */

/**
 * Collapse runs of three or more `$` into one.
 *
 * THE CORRUPTION HEALS ON READ. A value already stored as `$$$hello$$$` came
 * from an earlier round of the bug below; normalising on the way into the field
 * means the next edit writes it back clean rather than adding a fourth layer.
 *
 * Two `$$` are left alone — that is display maths, and `MATH_RENDER_OPTIONS`
 * declares it. Three or more is never valid in any convention, so it is always
 * damage.
 */
function collapse(v) {
  return v.replace(/\${3,}/g, '$');
}

/** Strip every `$` from the ends of a single maths run, however many there are. */
function bare(v) {
  return v.replace(/^\$+/, '').replace(/\$+$/, '');
}

/** What goes INTO the field. Text mode understands `$…$`, so only the healing. */
export function toMathValue(e) {
  if (!e) return '';
  const content = e.content == null ? '' : String(e.content);
  return collapse(content);
}

/**
 * What comes OUT of the field, converted back to the contract's form.
 *
 * Walks the LaTeX matching braces, because `\text{}` groups nest — `\text{a {b}
 * c}` is one group, and counting to the first `}` would end it early and mangle
 * everything after.
 *
 * A run outside any `\text{}` is maths and is wrapped in `$…$`.
 *
 * ── WHY IT COMPOUNDED ──────────────────────────────────────────────────────
 * It used to wrap a maths run WHATEVER it already contained. When MathLive
 * returned a value carrying our own delimiters — `\text{}$x$` — the run `$x$`
 * was wrapped again as `$$x$$`, and every resubmit added another pair. Three
 * rounds of editing produced `$$$hello$$$`.
 *
 * `bare()` is the fix: strip whatever delimiters a run arrives with, then add
 * exactly one pair. That makes the whole function IDEMPOTENT — running it on
 * its own output changes nothing, which is the property it needed and did not
 * have.
 */
export function fromMathValue(value) {
  const v = collapse(value == null ? '' : String(value));
  if (!v) return '';

  if (!v.includes('\\text{')) {
    const inner = bare(v);
    return inner ? `$${inner}$` : '';
  }

  let out = '';
  let math = '';
  let i = 0;

  const flushMath = () => {
    const t = bare(math.trim());
    if (t) out += `$${t}$`;
    math = '';
  };

  while (i < v.length) {
    if (v.startsWith('\\text{', i)) {
      flushMath();
      i += 6;
      let depth = 1;
      let text = '';
      while (i < v.length && depth > 0) {
        if (v[i] === '\\' && (v[i + 1] === '{' || v[i + 1] === '}')) {
          text += v[i + 1]; i += 2; continue;
        }
        if (v[i] === '{') depth += 1;
        else if (v[i] === '}') { depth -= 1; if (!depth) { i += 1; break; } }
        text += v[i];
        i += 1;
      }
      out += text;
      continue;
    }
    math += v[i];
    i += 1;
  }
  flushMath();
  return out;
}

// ── ROLE ────────────────────────────────────────────────────────────────────
/**
 * WHAT A LINE IS, as opposed to where it came from.
 *
 * `origin` records a fact about the page — printed or handwritten. `role`
 * records what the line DOES, and it is what a resubmit is built from: the
 * problem and the instructions describe the question, the work is what the
 * student did, the answer is what they concluded. One undifferentiated blob
 * cannot be asked three different questions.
 *
 * BOTH SURVIVE. Role is derived from origin and position, but a student edits
 * the role, not the origin — a misread line is fixed by saying what it is.
 *
 * THE DEFAULT ONLY HAS TO BE PLAUSIBLE. The student can change it, so the guess
 * should fail in a way they notice rather than being subtly wrong.
 */
export const ROLE_OPTIONS = [
  // `short` is what the dropdown shows on a phone. The section heading two
  // lines up already says "Your work", so repeating it on every row costs 24px
  // of field width to say nothing.
  { value: 'problem', label: 'Problem', short: 'Problem' },
  { value: 'work', label: 'Your work', short: 'Work' },
  { value: 'answer', label: 'Your answer', short: 'Answer' },
];

// Display order on the card. Instructions sit above these, in their own array.
export const ROLE_ORDER = ['problem', 'work', 'answer'];

/**
 * The dropdown's options, which include one that is NOT a role.
 *
 * `instruction` is not stored as a role and never will be — instructions live in
 * their own array with their own ids, because `instructions_used` refers to them
 * by id. It appears here because MOVING a line between the two stores is the
 * same gesture as changing its role, from the student's side, and giving it a
 * different control would be arbitrary.
 *
 * The transcriber mistakes a problem for an instruction often enough that
 * retyping it was the alternative.
 */
export const INSTRUCTION_OPTION = { value: 'instruction', label: 'Instructions', short: 'Instr.' };

export const MOVE_OPTIONS = [INSTRUCTION_OPTION, ...ROLE_OPTIONS];

export const ROLE_TITLES = {
  problem: 'Problem',
  work: 'Your work',
  answer: 'Your answer',
};

/**
 * An element's role, defaulting when the server did not send one.
 *
 * Printed lines came off the page, so they are the problem. Anything else is
 * the student's, and unlabelled student writing is work rather than an answer —
 * the safer guess, since a stray mark misfiled as an answer would be compared
 * against the real one.
 */
export function roleOf(e) {
  if (!e) return 'work';
  if (e.role && ROLE_TITLES[e.role]) return e.role;
  return e.origin === 'printed' ? 'problem' : 'work';
}

/**
 * The parts of a problem, in page order. `6a`, `6b`, `6c` inside problem `6`.
 *
 * EMPTY WHEN THERE ARE FEWER THAN TWO. That is deliberate: it lets every caller
 * be one line — `if (!parts.length) return <the ordinary path>` — instead of
 * carrying a separate "is this multi-part" boolean that can drift from the list
 * it was derived from.
 *
 * The problem's OWN label is the shared stem, not a part.
 *
 * Identical to `partsOf` in the server's `services/transcribe.js`. The two must
 * not diverge.
 */
export function partsOf(problem) {
  const label = problem && problem.label;
  const out = [];
  for (const e of (problem && problem.elements_array) || []) {
    if (e.label && e.label !== label && !out.includes(e.label)) out.push(e.label);
  }
  return out.length > 1 ? out : [];
}

/**
 * Every instruction in force for a part.
 *
 * A part sees the general rules, plus anything that took effect at it or at an
 * EARLIER part. `6c` sees general, `6a`'s, `6b`'s and its own — a teacher may
 * write anything anywhere, and deciding which rules actually apply is the
 * model's job rather than a filter's.
 *
 * ── THE MIDDLE CLAUSE IS NOT OPTIONAL ──────────────────────────────────────
 * An instruction whose `fromStep` names a part that NO LONGER EXISTS — the
 * student deleted it — matches neither of the other two clauses and would be
 * filtered out of every stream. The card would still show it; the model would
 * never receive it. A student reads "take gravity as 32.2" on screen, believes
 * it applied, and the answer was worked without it, with nothing reporting the
 * divergence.
 *
 * It was written for THIS problem. The part it named is gone. Applying it
 * everywhere is the safe reading; dropping it silently is the one failure a
 * student cannot see.
 *
 * Identical to `instructionsFor` in the server's `services/transcribe.js`.
 */
export function instructionsFor(problem, part, parts) {
  const ins = (problem && problem.instructions) || [];
  if (!part) return ins;

  const earlier = parts.slice(0, parts.indexOf(part) + 1);
  return ins.filter((i) => (
    !i.fromStep                        // general to the whole problem
    || !parts.includes(i.fromStep)     // orphaned — the part it named is gone
    || earlier.includes(i.fromStep)    // in force from here
  ));
}

export function roleLabel(role, narrow) {
  const found = ROLE_OPTIONS.find((o) => o.value === role) || ROLE_OPTIONS[1];
  return narrow ? found.short : found.label;
}

export const roleRank = (r) => {
  const i = ROLE_ORDER.indexOf(r);
  return i === -1 ? 1 : i;    // anything unrecognised behaves as work
};

/**
 * Every line's role, across a whole problem.
 *
 * `roleOf` decides one line at a time and cannot see the shape of the page.
 * THE ANSWER NEEDS THE WHOLE ARRAY: it is the student's LAST line, and last is
 * only knowable by looking at all of them.
 *
 * THE LAST LINE, NOT THE LAST RUN. A first attempt took the trailing run of
 * handwriting — but every line a student writes after the printed problem is
 * one contiguous run, so the whole of the working became the answer. Nothing in
 * `origin` separates working from answer; only position does.
 *
 * A MULTI-LINE ANSWER IS ORDINARY — a system of equations resolves to two lines
 * — but it cannot be DETECTED, only declared. The student marks the second line
 * as an answer too, and both travel.
 *
 * ONLY WHEN NOTHING WAS SENT. If the server assigned roles it has already
 * decided, and second-guessing here would overwrite a student's own correction
 * the moment their resubmit came back.
 *
 * Returns a NEW array; never mutates its argument.
 */
export function assignRoles(elements) {
  const list = elements || [];
  const out = list.map((e) => ({ ...e, role: roleOf(e) }));

  const sent = list.some((e) => e && e.role);
  if (sent) return out;

  // ── THE STUDENT'S LAST LINE, PER PART ─────────────────────────────────────
  //
  // Printed content below the working means the working has ended, so nothing
  // there is an answer.
  //
  // GROUPED BY LABEL FIRST. Applied to the whole array, this marks the last
  // line of part 7a's working as the answer to ALL of problem 7 — and 7b's own
  // answer never gets marked at all. Each part has its own last line.
  //
  // Ungrouped elements share the '' key, which is every ordinary problem, so
  // the loop below runs exactly once over exactly the same list it always did.
  const byLabel = new Map();
  for (const e of out) {
    const k = e.label || '';
    if (!byLabel.has(k)) byLabel.set(k, []);
    byLabel.get(k).push(e);
  }

  for (const group of byLabel.values()) {
    for (let i = group.length - 1; i >= 0; i -= 1) {
      if (group[i].role !== 'work') break;
      group[i].role = 'answer';
      break;
    }
  }

  return out;
}

/**
 * Move a line into another role, keeping the order it was written in.
 *
 * THE PAGE READS TOP TO BOTTOM AND SO DOES THE ARRAY. A line sent DOWN a
 * section was the last thing before that section began, so it lands at the TOP
 * of its destination. A line sent UP was the first thing after the previous
 * section ended, so it lands at the BOTTOM. Either way the student's own
 * sequence survives — and moving a line and moving it straight back returns it
 * to exactly where it started.
 *
 * Returns a NEW array.
 */
export function moveToRole(elements, index, to) {
  const list = elements || [];
  const line = list[index];
  if (!line || roleOf(line) === to) return list;

  const down = roleRank(to) > roleRank(roleOf(line));
  const rest = list.filter((_, i) => i !== index);
  const moved = { ...line, role: to };

  let at = -1;
  rest.forEach((l, i) => {
    if (roleOf(l) !== to) return;
    if (down) { if (at === -1) at = i; }   // before the first of the destination
    else at = i + 1;                        // after the last of it
  });

  // An empty destination: sit where that role belongs in the sequence.
  if (at === -1) {
    const after = rest.findIndex((l) => roleRank(roleOf(l)) > roleRank(to));
    at = after === -1 ? rest.length : after;
  }

  rest.splice(at, 0, moved);
  return rest;
}

/**
 * Where `+ line` inserts inside a section: after the last line already in that
 * role, or — if the section is empty — at the point where the role belongs.
 * A step added to Your work lands after the last step, not after the answer.
 */
export function insertIndexForRole(elements, role) {
  const list = elements || [];
  let last = -1;
  list.forEach((l, i) => { if (roleOf(l) === role) last = i + 1; });
  if (last !== -1) return last;
  const after = list.findIndex((l) => roleRank(roleOf(l)) > roleRank(role));
  return after === -1 ? list.length : after;
}

// ── NEW ROWS ────────────────────────────────────────────────────────────────
/**
 * What `+ line` creates.
 *
 * `math`, `handwritten` and `work`, because a student adding a line is adding a
 * step of their own working. `math` also means it gets a math-field, which is
 * what they want for a step.
 *
 * Lives here rather than in the card because it is element-shape knowledge, and
 * the `kind` it picks has to agree with the two functions above.
 */
export function newElement(role = 'work', label = null) {
  // `label` IS THE PART. A line added while tab `7b` is showing belongs to 7b,
  // and without it the line is an orphan — `problemText` files an unlabelled
  // element with the STEM, so the student's new working would be presented to
  // the model as part of the question.
  //
  // Null on a problem with no parts, which is every ordinary one.
  return { kind: 'math', origin: 'handwritten', struck: false, role, label, content: '' };
}

/**
 * What `+ instruction` creates, with an id unique within THIS problem.
 *
 * Instruction ids are problem-scoped and start at `i1`. `i1` on problem 5 and
 * `i1` on problem 6 are independent — nothing ever compares across problems.
 *
 * A resubmit prefixes ids with `r` (`i2` becomes `ri2`), so an array being
 * edited may already hold `ri1`, `ri2`. The regex strips leading `r`s to find
 * the highest number, so the next id is `i3` — UNPREFIXED, because this
 * instruction is new in this round and has not survived one yet. It picks up
 * its own `r` when this round is resubmitted.
 */
export function nextInstructionId(instructions) {
  let max = 0;
  for (const ins of instructions || []) {
    const m = /^r*i(\d+)$/.exec((ins && ins.id) || '');
    if (m) {
      const n = parseInt(m[1], 10);
      if (n > max) max = n;
    }
  }
  return `i${max + 1}`;
}

export function newInstruction(instructions, fromStep = null) {
  // `fromStep` IS THE PART IT TAKES EFFECT FROM, null meaning general to the
  // whole problem. Null is the right default: a student adding an instruction
  // is adding a general one unless they say otherwise, and the dropdown on the
  // row is where they say otherwise.
  return { id: nextInstructionId(instructions), content: '', fromStep };
}

// ── THE ORIGIN DROPDOWN ─────────────────────────────────────────────────────
/**
 * The student never sees our vocabulary. `printed` and `handwritten` are what
 * travels; these are what the dropdown shows.
 *
 * This is the important control, not the text field — the characteristic
 * transcription error is a line attributed to the wrong layer.
 */
export const ORIGIN_OPTIONS = [
  { value: 'printed', label: 'on the sheet' },
  { value: 'handwritten', label: 'my handwriting' },
];

export function originLabel(origin) {
  const found = ORIGIN_OPTIONS.find((o) => o.value === origin);
  return found ? found.label : ORIGIN_OPTIONS[1].label;
}
